<div class="container">
  <h4>How to setup Alert Actions in Axis Camera Station</h4>
  <p>
    This guide will walk you through the steps to setup Action Rules in Axis
    Camera Station to trigger Camera Alerts based on thermal events. The
    instructions and screenshots are based on Axis Camera Station Pro version
    6.1, and may be different for other versions.
  </p>
  <div class="mb-2">
    <label for="cboAlert">Which alert are you setting up?</label>
    <select
      id="cboAlert"
      class="form-control form-select"
      [(ngModel)]="selectedAlert"
      (change)="changeAlert()">
      <option [ngValue]="null">Select an alert</option>
      @for (alert of this.adminService.alerts; track alert; let i = $index) {
        <option [ngValue]="alert">
          {{ alert.Name }}
        </option>
      }
    </select>
  </div>
  <hr />
  <div ngbAccordion #accordion="ngbAccordion" [closeOthers]="true">
    <div
      ngbAccordionItem="actionIntroduction"
      #actionIntroduction="ngbAccordionItem"
      [collapsed]="false">
      <h2 ngbAccordionHeader>
        <button ngbAccordionButton>Introduction</button>
      </h2>
      <div ngbAccordionCollapse>
        <div ngbAccordionBody class="help-container">
          <ng-template>
            <p>
              The Camera Alerts are triggered by the Axis Camera Station server
              using Action Rules. This is a step by step guide to setup those
              Action Rules in Axis Camera Station.
            </p>
            <p>
              If you have not already, you will need to create a Camera Alert.
              Click
              <a
                type="button"
                class="btn-link fw-bold"
                [routerLink]="['add']"
                [disabled]="!adminService.isReady"
                >here</a
              >
              to create a new alert.
            </p>
            <hr />
            <button
              type="button"
              [disabled]="selectedAlert === null"
              class="btn btn-primary btn-sm"
              (click)="createActionRule.expand()">
              Continue
            </button>
          </ng-template>
        </div>
      </div>
    </div>
    <div
      ngbAccordionItem="createActionRule"
      #createActionRule="ngbAccordionItem">
      <h2 ngbAccordionHeader>
        <button ngbAccordionButton>Create New Action Rule</button>
      </h2>
      <div ngbAccordionCollapse>
        <div ngbAccordionBody class="help-container">
          <ng-template>
            <p>
              An Action Rule is what defines the triggers and actions that will
              be used to trigger the alert and notify recipients. You will need
              to create an Action Rule for each alert you want to create, and
              each Action Rule can have multiple triggers if needed.
            </p>
            <h4>Create the Action Rule</h4>
            <p>
              <img
                ngSrc="assets/images/alerts/ActionRuleMenu.png"
                width="552"
                height="379" />
            </p>
            <p>
              In the Axis Camera Station client, open a
              <strong>Configuration</strong>
              tab and navigate to
              <strong>Recordings and events -> Action rules</strong>. Click the
              <strong>New</strong> button in the lower right to create a new
              Action Rule. Give the Action Rule a name and you are ready to add
              Triggers.
            </p>
            <hr />
            <button
              type="button"
              class="btn btn-primary btn-sm"
              (click)="addTriggers.expand()">
              Next
            </button>
          </ng-template>
        </div>
      </div>
    </div>
    <div ngbAccordionItem="addTriggers" #addTriggers="ngbAccordionItem">
      <h2 ngbAccordionHeader>
        <button ngbAccordionButton>Add Triggers</button>
      </h2>
      <div ngbAccordionCollapse>
        <div ngbAccordionBody class="help-container">
          <ng-template>
            <p>
              The triggers define the events that will cause the alert to be
              triggered. For a thermal alert, you will need to select a thermal
              camera and use the "Thermal Detection" event from the camera, and
              then specify which temperature zone you want to use as a trigger.
            </p>
            <p>
              A camera can have more than one temperature zone. You can create
              multiple triggers for the same alert if you want to use more than
              one zone, or zones from multiple cameras. These zones are
              configured in the camera and must be setup before creating the
              triggers.
            </p>
            <h4>Create the Triggers</h4>
            <p>
              <img
                ngSrc="assets/images/alerts/TriggerSection.png"
                width="639"
                height="284" />
            </p>
            <p>
              In the <strong>Trigger</strong> section, click the
              <strong>Add</strong> button.
            </p>
            <p>
              <img
                ngSrc="assets/images/alerts/AddTrigger.png"
                width="542"
                height="452" />
            </p>
            <p>
              In the Add Trigger dialog, select
              <strong>Device Event</strong>
              and click OK.
            </p>
            <p>
              <img
                ngSrc="assets/images/alerts/CreateNew.png"
                width="461"
                height="572" />
            </p>

            In the
            <strong>Create Device Event Trigger</strong> dialog:
            <ul>
              <li>Select the camera you want to use for the trigger.</li>
              <li>
                For the <strong>Event</strong>, select
                <strong>Thermal Detection</strong>.
              </li>
              <li>
                In the <strong>Filters</strong> section:
                <ul>
                  <li>
                    Choose the <strong>Temperature Zone</strong> to use for the
                    Trigger. If there are more than one, you will need to create
                    multiple Triggers, one for each Zone if you want them to
                    trigger this alert.
                  </li>
                  <li>
                    The <strong>token</strong> will normally be "1", and set
                    <strong>Alarm active</strong> to "Yes".
                  </li>
                </ul>
              </li>
            </ul>

            <p>Add additional Triggers as needed.</p>

            <hr />
            <button
              type="button"
              class="btn btn-primary btn-sm"
              (click)="addAction.expand()">
              Next
            </button>
          </ng-template>
        </div>
      </div>
    </div>
    <div ngbAccordionItem="addAction" #addAction="ngbAccordionItem">
      <h2 ngbAccordionHeader>
        <button ngbAccordionButton>Add Action</button>
      </h2>
      <div ngbAccordionCollapse>
        <div ngbAccordionBody class="help-container">
          <ng-template>
            <p>
              This is the action which will notify the Camera Alert service that
              an alert has been triggered, and allow it to update the alert
              status and notify recipients.
            </p>
            <h4>Create the Action</h4>
            <p>
              <img
                ngSrc="assets/images/alerts/ActionSection.png"
                width="640"
                height="292" />
            </p>
            <p>
              In the <strong>Action</strong> section, click the
              <strong>Add</strong>
              button.
            </p>
            <p>
              <img
                ngSrc="assets/images/alerts/AddAction.png"
                width="516"
                height="422" />
            </p>
            <p>
              In the <strong>Add Action</strong> dialog, select
              <strong>Send HTTP Notification</strong>, and click OK.
            </p>
            <p>
              <img
                ngSrc="assets/images/alerts/EditAction.png"
                width="514"
                height="523" />
            </p>
            In the
            <strong>Create Send HTTP Notification Action</strong>
            dialog:
            <ul>
              <li>
                Set the <strong>URL</strong> to:
                <div
                  style="
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 1rem;
                  ">
                  <ngb-alert [dismissible]="false" [type]="'success'">
                    <span id="actionUrl">
                      https://valcoatweb/acs-api/acs/events/trigger?alertId={{
                        selectedAlert?.Id
                      }}
                    </span>
                    <button
                      type="button"
                      title="Copy Url to Clipboard"
                      class="btn btn-link"
                      (click)="copyUrlToClipboard()">
                      <fa-icon [icon]="icons.copy"></fa-icon>
                    </button>
                  </ngb-alert>
                  <ngb-alert [dismissible]="false" [type]="'warning'">
                    Note: This URL is specific to the
                    <strong>{{ selectedAlert?.Name }}</strong> alert
                    configuration. Make sure to copy the URL for each alert you
                    create.
                  </ngb-alert>
                </div>
              </li>
              <li>No authentication is required.</li>
              <li>
                Click the <strong>Advanced</strong> button.
                <ul>
                  <li>Change the <strong>Method</strong> to "POST".</li>
                  <li>
                    Change the <strong>Content Type</strong> to
                    "application/json".
                  </li>
                  <li>Leave the <strong>Body</strong> empty.</li>
                </ul>
              </li>
              <li>Click <strong>OK</strong> to save the Action.</li>
            </ul>
            <hr />

            <button
              type="button"
              class="btn btn-primary btn-sm"
              (click)="complete.expand()">
              Next
            </button>
          </ng-template>
        </div>
      </div>
    </div>
    <div ngbAccordionItem="complete" #complete="ngbAccordionItem">
      <h2 ngbAccordionHeader>
        <button ngbAccordionButton>Finish Up</button>
      </h2>
      <div ngbAccordionCollapse>
        <div ngbAccordionBody class="help-container">
          <ng-template>
            <p>
              Once the Action is created, review the Action Rule and make sure
              to click <strong>Save</strong> to save the changes to the Action
              Rule. The alert should now be ready to trigger when the conditions
              in the triggers are met.
            </p>
            <hr />
            <a class="btn btn-primary btn-sm" [routerLink]="['../']"
              >Done. Return to Alerts.</a
            >
          </ng-template>
        </div>
      </div>
    </div>
    <!--
              <div ngbAccordionItem>
                <h2 ngbAccordionHeader>
                  <button ngbAccordionButton>Introduction</button>
                </h2>
                <div ngbAccordionCollapse>
                  <div ngbAccordionBody>
                    <ng-template> </ng-template>
                  </div>
                </div>
              </div>
              -->
  </div>
</div>
